import React, { useState, useRef, useEffect } from 'react';
import './Survey.css';
import { NormalButton } from '../../api/components-examples/ReusableCode/EmlyButton';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { getCurrentContextuserIdDetails } from '../../api/components-examples/Admin/AccountUtility';
import { LoaderBGWithinComponent } from '../../api/components-examples/utils';
import { addNotification } from '../../api/redux/notificationAction';
import { showNotification } from '../../api/redux/EmlyNotificationReducer';

// Fallback questions for LEARNER plan
const learnerSurveyData = [
    {
        id: 1,
        category: 'LEARNING STYLE',
        question: 'What is your preferred method of learning?',
        options: ['Videos', 'Books', 'Hands-on Projects', 'Live Workshops'],
    },
    {
        id: 2,
        category: 'GOALS',
        question: 'What do you hope to achieve with EmlyLabs?',
        options: ['Career advancement', 'Learning new skills', 'Building projects', 'Exploring technology'],
    },
    {
        id: 3,
        category: 'TECHNOLOGY INTEREST',
        question: 'Which area of technology are you most interested in?',
        options: ['AI/ML', 'Web Development', 'Data Science', 'Cloud Computing'],
    },
    {
        id: 4,
        category: 'EXPERIENCE',
        question: 'What is your current level of experience in tech?',
        options: ['Beginner', 'Intermediate', 'Advanced', 'Expert'],
    },
    {
        id: 5,
        category: 'LEARNING PACE',
        question: 'How much time can you dedicate to learning each week?',
        options: ['Less than 2 hours', '2–5 hours', '5–10 hours', '10+ hours'],
    },
];

// How long to show the selected state before advancing
const ADVANCE_DELAY_MS = 200; // tweak 400–800ms for feel

const Survey = function (props) {
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [selectedSurveyData, setSelectedSurveyData] = useState([]);
    const [answers, setAnswers] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    // NEW: temporarily lock after a click so selection is visible
    const [isAdvancing, setIsAdvancing] = useState(false);
    const advanceTimerRef = useRef(null);

    const current_logged_user = getCurrentContextuserIdDetails(
        props.myUserAccountInfo && props.myUserAccountInfo.current_logged_user
    );

    useEffect(() => {
        setIsLoading(true);
        const api = props.api;
        if (api == null) {
            setSelectedSurveyData(learnerSurveyData);
            setIsLoading(false);
            return;
        }
        const action = api.getSurveyQuestionsRSAA(props.planType);
        Promise.resolve(props.dispatch(action))
            .then((response) => {
                const failure = response.type.toLowerCase().endsWith('failure');
                if (!failure && response.payload && Array.isArray(response.payload) && response.payload.length) {
                    setSelectedSurveyData(response.payload);
                } else {
                    setSelectedSurveyData(learnerSurveyData);
                }
            })
            .finally(() => {
                setIsLoading(false);
            });

        // cleanup on unmount
        return () => {
            if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const currentQuestion = selectedSurveyData[currentQuestionIndex];
    const selectedOption = currentQuestion ? answers[currentQuestion.id] : null;
    const isLastQuestion =
        selectedSurveyData.length > 0 &&
        currentQuestionIndex === selectedSurveyData.length - 1;

    const handleOptionClick = function (option) {
        if (!currentQuestion) return;
        if (isAdvancing) return; // ignore extra clicks during dwell period

        // Save answer immediately so the selected option highlights
        setAnswers((prev) => ({ ...prev, [currentQuestion.id]: option }));

        // If it's the last question, don't auto-advance (Submit button will appear)
        if (isLastQuestion) return;

        // Briefly lock the UI and then advance
        setIsAdvancing(true);
        if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
        advanceTimerRef.current = setTimeout(() => {
            setCurrentQuestionIndex((idx) => idx + 1);
            setIsAdvancing(false);
        }, ADVANCE_DELAY_MS);
    };

    const handleBackClick = function () {
        // Cancel any pending advance when going back
        if (advanceTimerRef.current) {
            clearTimeout(advanceTimerRef.current);
            advanceTimerRef.current = null;
        }
        setIsAdvancing(false);
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex((idx) => idx - 1);
        }
    };

    const mapAnswersToQuestions = function (answersObj) {
        return selectedSurveyData
            .filter((q) => answersObj[q.id])
            .map((q) => ({
                question: q.question,
                answer: answersObj[q.id],
            }));
    };

    const submitSurvey = function () {
        const surveyQA = mapAnswersToQuestions(answers);
        const body = { survey: surveyQA };
        const api = props.api;

        if (!api) return;

        setIsLoading(true);

        const action = api.createFetchUpdateProfileRSAA(current_logged_user, body);

        Promise.resolve(props.dispatch(action))
            .then(function (response) {
                const hasError = response.type.toLowerCase().indexOf('failure') !== -1;

                if (!hasError) {
                    props.dispatch({
                        type: 'FETCH_MY_PROFILE_SUCCESS',
                        payload: {
                            ...props.myProfile,
                            meta: { survey: surveyQA },
                        },
                    });
                } else {
                    const message =
                        response &&
                            response.payload &&
                            response.payload.response &&
                            response.payload.response.message
                            ? response.payload.response.message
                            : window.REACT_APP_ERROR_MESSAGE;

                    props.dispatch(addNotification(message, 'error', false));
                    props.dispatch(showNotification(true));
                }
            })
            .finally(function () {
                setIsLoading(false);
            });
    };

    return (
        <div className="survey-container-wrapper">
            {isLoading && <LoaderBGWithinComponent />}
            <div className="survey-container">
                <div className="survey-content">
                    <h1 className="survey-welcome-header">Welcome!</h1>

                    <div className="survey-progress-bar" aria-hidden="true">
                        {selectedSurveyData.map((_, index) => (
                            <div
                                key={index}
                                className={
                                    'survey-progress-step ' + (index <= currentQuestionIndex ? 'active' : '')
                                }
                            />
                        ))}
                    </div>

                    {currentQuestion && (
                        <div
                            className={
                                'survey-form ' +
                                (isAdvancing ? 'survey-form--locked' : 'survey-form--ready')
                            }
                            aria-live="polite"
                        >
                            <p className="survey-category">{currentQuestion.category}</p>
                            <h2 className="survey-question">{currentQuestion.question}</h2>

                            <div className="survey-options-grid">
                                {currentQuestion.options.map(function (option) {
                                    const isSelected = selectedOption === option;
                                    return (
                                        <button
                                            key={option}
                                            className={
                                                'survey-option-button ' +
                                                (isSelected ? 'selected ' : '') +
                                                (isAdvancing ? 'locked ' : '')
                                            }
                                            onClick={() => handleOptionClick(option)}
                                            aria-pressed={isSelected}
                                            disabled={isAdvancing}
                                        >
                                            <span className="survey-option-label">{option}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className="survey-completion-screen-actions">
                        {currentQuestionIndex > 0 && (
                            <NormalButton
                                className="em-btn survey-back-button"
                                onClick={handleBackClick}
                                disabled={isAdvancing}
                            >
                                Back
                            </NormalButton>
                        )}
                        {isLastQuestion &&
                            selectedSurveyData.length > 0 &&
                            Object.keys(answers).length === selectedSurveyData.length && (
                                <NormalButton className="em-btn" onClick={submitSurvey} disabled={isAdvancing}>
                                    Get Started
                                </NormalButton>
                            )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = function (state) {
    return {
        api: state.api,
        myUserAccountInfo: state.myUserAccountInfo,
        myProfile: state.myProfile,
    };
};

export default withRouter(connect(mapStateToProps)(Survey));
